import { IconButton } from '@artosera/ui';
import type { CibleRattachement } from '@artosera/shared';
import { CalendarRange, Contact, Image, Receipt, Users, X } from 'lucide-react';
import type { ComponentType, JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useRattachementResolu, type RattachementLu } from './useRattachementResolu.js';
import styles from './RattachementPastille.module.css';

const ICONES: Record<
  CibleRattachement,
  ComponentType<{ size?: number; 'aria-hidden'?: boolean }>
> = {
  artwork: Image,
  artist: Users,
  contact: Contact,
  exhibition: CalendarRange,
  movement: Image,
  invoice: Receipt,
  edition: Image,
  post: Image,
};

export interface RattachementPastilleProps {
  galerieSlug: string;
  rattachement: RattachementLu;
  /** Présent : la pastille devient une puce retirable (dialogue de dépose). */
  onRetirer?: () => void;
}

/**
 * Rattachement cliquable (3.3, écran 4) : mène à la fiche œuvre, artiste,
 * contact ou exposition visée. Le libellé se résout côté client
 * (`useRattachementResolu`) — jusqu'à sa réponse, la pastille affiche
 * l'identifiant plutôt que de clignoter à vide.
 */
export function RattachementPastille({
  galerieSlug,
  rattachement,
  onRetirer,
}: RattachementPastilleProps): JSX.Element {
  const { t } = useTranslation(['regie', 'commun']);
  const resolu = useRattachementResolu(galerieSlug, rattachement);
  const Icone = ICONES[rattachement.type];
  const contenu = resolu.chargement
    ? t('commun:chargement')
    : (resolu.label ?? t('regie:coffre.rattachements.introuvable'));

  return (
    <span className={styles.pastille}>
      <Link className={styles.lien} to={resolu.chemin}>
        <Icone size={12} aria-hidden />
        <span className={styles.libelle}>{contenu}</span>
      </Link>
      {onRetirer ? (
        <IconButton
          className={styles.retirer}
          icon={<X aria-hidden />}
          aria-label={t('regie:coffre.rattachements.retirer', { rattachement: contenu })}
          size="sm"
          onClick={onRetirer}
        />
      ) : null}
    </span>
  );
}
